import { useTheme, Box, Button, Card, CardContent, CardHeader, Divider, FormHelperText, TextField as MuiTextField, Grid, MenuItem } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react'
import { SaveAction } from '~/dataProvider/types';
import { MfaWay, OptionResponseDto, UserResponseDto } from '~/gen/api/user';
import * as Yup from 'yup'
import ChangePhone from './ChangePhone';
// import PhoneInput from 'react-phone-number-input'
// import CustomPhoneNumber from '~/components/phoneInput/phoneInput'
// import 'react-phone-number-input/style.css';
import { useFormik } from "formik";
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import SelectField from '../formik/SelectField';
import { languageOptions } from '~/utils/lang-options';
import { useGetMainList } from 'ra-core';
import { pages, sorts } from '~/utils/paramsWithGetMainList';
import { RecPatch } from '~/dataProvider/tools';


const initialValues = {
  submit: '',
  firstName: '',
  lastName: '',
  mfaWay: MfaWay.Email as number,
  language: 'en-US', // navigator.language,
  timezone: '' as '' | OptionResponseDto,
}

export type FormValues = typeof initialValues

export interface InnerFormExtendProps { 
  user?: UserResponseDto, 
  idTokenUser: any,
}

const validationSchema =
  Yup.object().shape({
    firstName: Yup.string().max(50)
      .required('First Name is required'),
    lastName: Yup.string().max(50)
      .required('Last Name is required'),
  })

export const ProfileUpdateForm: FC<{ save: SaveAction<FormValues> } & InnerFormExtendProps> = (props) => {
  const {
    save,
    user,
    idTokenUser,
  } = props
  const theme = useTheme()
  const { data: timezoneBag } = useGetMainList<OptionResponseDto & RecPatch>('timezone-options', pages(), sorts(), {})
  const timezoneOptions = Object.values(timezoneBag || {}).sort((a, b) => { return a.value !== b.value ? ((+(a.value ?? 0)) - (+(b.value ?? 0))) : ((a.name ?? '') < (b.name ?? '') ? -1 : 1)  })
  
  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    save({...values }, '', options)
  }, [save])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, touched, isSubmitting, setFieldValue } = formikCtrl

  useEffect(() => {
    if (!touched.firstName && user?.firstName) {
      setFieldValue('firstName', user.firstName)
    }
    if (!touched.lastName && user?.lastName) {
      setFieldValue('lastName', user.lastName)
    }
    if (!touched.mfaWay && user?.mfaWay) {
      setFieldValue('mfaWay', user.mfaWay)
    }
  }, [touched.firstName, touched.lastName, touched.mfaWay, user, setFieldValue])

  useEffect(() => {

    if (timezoneOptions.length > 0) {
      if (user?.timeZoneName) {
        const currTimezone = timezoneOptions.find(t => t.name === user.timeZoneName)
        currTimezone && setFieldValue('timezone', currTimezone)
      } else {
        const offset = ((n) => `${n < Math.abs(n) || n === 0 ? '' : '-'}${Math.abs(n)}`)(new Date().getTimezoneOffset());
        const timezoneName = getStandardTimezoneName()
        const currTimezone = 
          timezoneOptions.find(t => t.name === timezoneName) ??
          timezoneOptions.find(t => t.value === offset)
        currTimezone && setFieldValue('timezone', currTimezone)
      }
    }

    if (user?.language) {
      setFieldValue('language', user.language)
    }
  }, [user?.timeZoneName, user?.language, timezoneOptions.length, setFieldValue])

  const [newPhone, setNewPhone] = useState('')

  return (
    <OdForm formikCtrl={formikCtrl}>

      <Card>
        <CardHeader title="Profile" />
        <Divider />
        <CardContent>
          <Grid
            container
            spacing={4}
          >
            <Grid item md={6} xs={12} >
              <TextField label="First Name*" name="firstName" />
            </Grid>
            <Grid item md={6} xs={12} >
              <TextField label="Last Name*" name="lastName" />
            </Grid>

            <Grid item md={6} xs={12} >
              <MuiTextField
                id="email"
                fullWidth
                disabled
                label="Email Address"
                type="email"
                value={idTokenUser?.email || ''}
                variant="outlined"
              />
            </Grid>
            <Grid item md={6} sx={{display: {xs: 'none', md: 'block'}}} />

            <Grid item md={6} xs={12} >
              <MuiTextField
                id="phoneNum"
                disabled
                InputLabelProps={{ shrink: true }}
                fullWidth
                label="Phone Number*"
                value={newPhone || user?.phone || ''}
                variant="outlined"
              />
            </Grid>
            <Grid item md={6} xs={12} >
              <ChangePhone
                variant={theme.palette.mode === 'light' ? "contained" : "text"}
                color={theme.palette.mode === 'light' ? "inherit" : 'primary'}
                acceptNewPhone={setNewPhone}
              />
            </Grid>

            <Grid item md={6} xs={12} >
              <TextField
                label="Multi-Factor Authentication Preference*"
                name="mfaWay"
                select
              >
                <MenuItem value={0}>Email</MenuItem>
                <MenuItem value={1}>Phone</MenuItem>
              </TextField>
            </Grid>
            <Grid item md={6} sx={{display: {xs: 'none', md: 'block'}}} />

            <Grid item md={6} xs={12} >
              <MuiTextField
                id="user-roles"
                disabled
                fullWidth
                label="Role"
                value={
                  //@ts-ignore
                  (user && user['roles'] && user['roles'].map(r => r.name).join(', ')) 
                  || ''
                }
                variant="outlined"
              />
            </Grid>

            <Grid item md={6} xs={12} >
              <SelectField
                label="Preferred Language"
                name="language"
              >
                {languageOptions.map(l => (
                  <MenuItem key={l.tag} value={l.tag} >{l.name}</MenuItem>
                ))}
              </SelectField>
            </Grid>
            <Grid item md={6} xs={12} >
              <SelectField
                label="Time Zone"
                name="timezone"
              >
                {timezoneOptions.map(t => (
                  <MenuItem key={t.id} value={t as any} >{t.displayName}</MenuItem>
                ))}
              </SelectField>
            </Grid>

          </Grid>
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>
                {errors.submit}
              </FormHelperText>
            </Box>
          )}
        </CardContent>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 2
          }}
        >
          <Button
            color="primary"
            disabled={isSubmitting}
            type="submit"
            variant="contained"
          >
            Update
          </Button>
        </Box>
      </Card>

    </OdForm>
  )
}

const getStandardTimezoneName = () =>
  new Date()
      .toLocaleDateString('en-US', {
        day: '2-digit',
        timeZoneName: 'long',
      })
      .slice(4)

